import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, ApiError, setUnauthorizedHandler } from "../api/client";

const AuthContext = createContext({
  user: null,
  loading: true,
  authenticated: false,
  login: async () => false,
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await apiGet("/api/me");
      if (me?.authenticated) {
        setUser(me.user || { username: "" });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // On 401 anywhere in the app, drop the user state so the layout can show login.
    return setUnauthorizedHandler(() => setUser(null));
  }, [refresh]);

  const login = useCallback(async (username, password) => {
    try {
      const res = await apiPost("/api/login", { username, password }, { auth: false, csrf: false });
      if (res?.success) {
        setUser(res.user || { username });
        return { ok: true };
      }
      return { ok: false, error: res?.error || "Login failed" };
    } catch (e) {
      return { ok: false, error: e instanceof ApiError ? e.message : "Login failed" };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost("/api/logout", null, { auth: false, csrf: true });
    } catch { /* ignore — clear locally regardless */ }
    setUser(null);
  }, []);

  const value = React.useMemo(
    () => ({
      user,
      loading,
      authenticated: !!user,
      login,
      logout,
      refresh,
    }),
    [user, loading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
